// Atrium Resident Portal v0.4.0
import { StrictMode, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { collection, query, where, getDocs, documentId } from "firebase/firestore";
import { auth, db } from "./firebase";
import { api, type NewVehiclePayload, type Service as ApiService, type Booking, type ResidentPortalSettings } from "./api";
import "./index.css";
import PortalHeader from "./components/PortalHeader";
import PortalLoginCard from "./components/PortalLoginCard";
import AddVehicleForm, { type VehicleSuggestion } from "./components/AddVehicleForm";
import ProgressStepBar from "./components/ProgressStepBar";
import VehicleCard, { type Vehicle } from "./components/VehicleCard";
import Modal from "./components/Modal";
import ValetRequestForm from "./components/ValetRequestForm";
import ValetRequestSuccess from "./components/ValetRequestSuccess";
import ValetRequestsPage from "./ValetRequestsPage";
import ResidentFeedbackForm from "./components/ResidentFeedbackForm";
import { STEP_INDICES, STEPS } from "./progressSteps";

// Version constant
const PORTAL_VERSION = "0.3.5";

// Console log to force bundle change
console.log("BUILD_VERSION", PORTAL_VERSION + "-" + Date.now());

type Screen = "login" | "vehicle-select" | "service-select" | "slot-select" | "confirmation" | "success" | "add-vehicle" | "bookings" | "valet-request" | "valet-request-success" | "valet-requests" | "feedback";

interface CarWashService {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  priceCents: number;
  active: boolean;
}

interface Slot {
  slotId: string;
  startTime: string;
  endTime: string;
  display: string;
  available: boolean;
  capacityRemaining: number;
}

interface EditVehiclePayload {
  vehicleId: string;
  licensePlate: string;
  make: string;
  model: string;
  color: string;
}

function App() {
  // Public, unauthenticated guest-feedback routes are handled entirely in
  // main.tsx, which renders
  // pages/GuestFeedbackPage before <App/> ever mounts — this component is
  // only ever reached for the authenticated resident-portal experience.
  const { user, token, buildinggId, login, logout, unauthorized, loading: authLoading } = useAuth();
  const residentId = token?.residentId;
  const locationId = token?.locationId;

  const [screen, setScreen] = useState<Screen>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [services, setServices] = useState<CarWashService[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedService, setSelectedService] = useState<CarWashService | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [addVehicleSuccess, setAddVehicleSuccess] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [vehicleToEdit, setVehicleToEdit] = useState<Vehicle | null>(null);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [editForm, setEditForm] = useState<EditVehiclePayload | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [valetRequestId, setValetRequestId] = useState<string>("");

  // Resident Portal settings — sourced from the `getResidentPortalSettings`
  // callable. The callable returns a fully-normalized shape with everything
  // disabled by default, so:
  //   - During fetch: `portalSettings === null && portalSettingsLoading === true`
  //     → home renders a calm "Loading your services…" state.
  //   - After fetch success: every gate reads from the normalized doc.
  //   - On fetch failure: settings stays `null` and the home renders the
  //     conservative fallback ("Available services depend on your building
  //     settings.") — no fake flags, no inferred defaults.
  const [portalSettings, setPortalSettings] = useState<ResidentPortalSettings | null>(null);
  const [portalSettingsLoading, setPortalSettingsLoading] = useState<boolean>(false);

  // Render log at very top of component
  console.log("APP_RENDER", {
    userUid: user?.uid || null,
    hasUser: user !== null,
    residentId: token?.residentId || null,
    locationId: token?.locationId || null,
    buildinggId,
    screen,
    loading,
    authLoading,
    unauthorized,
  });

  // Log auth user info
  useEffect(() => {
    if (user) {
      console.log("PORTAL_AUTH_USER", {
        uid: user.uid,
        email: user.email,
      });
    }
  }, [user]);

  // Resident profile presence is derived synchronously from ID token claims
  // (see AuthContext.buildToken) — there is no separate async Firestore
  // lookup. Once authLoading is false, residentId/locationId being absent is
  // final for this session, not a transient state. These logs mark that
  // resolution for support/debugging without exposing it beyond the console.
  useEffect(() => {
    if (!user || authLoading) return;
    console.log("PROFILE_LOOKUP_STARTED", { uid: user.uid });
    const hasResidentProfile = Boolean(residentId && locationId);
    if (!hasResidentProfile) {
      console.log("PROFILE_LOOKUP_EMPTY", { uid: user.uid });
    }
    console.log("PROFILE_LOADING_RESOLVED", { uid: user.uid, hasResidentProfile });
  }, [user, authLoading, residentId, locationId]);

  // Post-login effect: change screen from "login" once auth is ready
  useEffect(() => {
    if (screen === "login" && user && residentId && locationId) {
      console.log("POST_LOGIN_SCREEN_TRANSITION", {
        from: screen,
        to: "vehicle-select",
        buildinggId,
        residentId,
        locationId,
      });
      setScreen("vehicle-select");
    }
  }, [screen, user, residentId, locationId, buildinggId]);

  // Fetch resident portal settings once auth + locationId are ready. The
  // backend reads locationId from JWT claims (we cannot pass another one in),
  // so the only inputs that matter here are auth-readiness signals. Fail-
  // conservative on error: settings stays null → fallback message renders.
  useEffect(() => {
    if (!user || !residentId || !locationId) return;
    let cancelled = false;
    setPortalSettingsLoading(true);
    api.getResidentPortalSettings()
      .then(res => {
        if (cancelled) return;
        setPortalSettings(res.data ?? null);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("PORTAL_SETTINGS_FETCH_FAILED", err?.message ?? String(err));
        setPortalSettings(null);
      })
      .finally(() => {
        if (cancelled) return;
        setPortalSettingsLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, residentId, locationId]);

  // Dismiss toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Fetch user's vehicles
  useEffect(() => {
    // Cancellation guard. Set to true on cleanup (unmount, sign-out, screen
    // change, location change). Every awaited Firestore call is followed by
    // a `cancelled` check, and the catch branch silently ignores errors that
    // arrive after cancellation. This prevents the post-sign-out
    // permission-denied banner: when the user signs out mid-flight, the
    // in-flight reads fail with Firestore rules but we ignore the result
    // and the noisy `PORTAL_READ_VEHICLES_ERROR` log.
    let cancelled = false;

    async function fetchVehicles() {
      console.log("PORTAL_LOAD_VEHICLES_START", { residentId, buildinggId, screen });
      if (!residentId || !buildinggId) {
        console.log("PORTAL_LOAD_VEHICLES_SKIP: Missing residentId or buildinggId");
        return;
      }

      setLoading(true);
      setLoadError(null);

      try {
        // Query residentVehicles links
        const linksRef = collection(db, "locations", buildinggId, "residentVehicles");
        const linksQuery = query(
          linksRef,
          where("residentId", "==", residentId),
          where("isActive", "==", true)
        );
        const linksSnapshot = await getDocs(linksQuery);
        if (cancelled) return;
        console.log("PORTAL_READ_OK_RESIDENT_VEHICLES", { count: linksSnapshot.size });

        // Collect vehicleIds
        const vehicleIds: string[] = [];
        const vehicleIdToPlate = new Map<string, string>();
        linksSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.vehicleId) {
            vehicleIds.push(data.vehicleId);
            if (data.licensePlate) {
              vehicleIdToPlate.set(data.vehicleId, data.licensePlate);
            }
          }
        });

        if (vehicleIds.length === 0) {
          if (cancelled) return;
          setVehicles([]);
          setLoading(false);
          return;
        }

        // Read vehicle docs
        const vehiclesRef = collection(db, "locations", buildinggId, "vehicles");
        const vehiclesQuery = query(
          vehiclesRef,
          where(documentId(), "in", vehicleIds.length <= 10 ? vehicleIds : vehicleIds.slice(0, 10))
        );
        const vehiclesSnapshot = await getDocs(vehiclesQuery);
        if (cancelled) return;
        console.log("PORTAL_READ_OK_VEHICLES", { count: vehiclesSnapshot.size });

        const vehicleList: Vehicle[] = [];
        vehiclesSnapshot.forEach((doc) => {
          const data = doc.data();
          vehicleList.push({
            id: doc.id,
            plate: data.licensePlate || vehicleIdToPlate.get(doc.id) || "",
            make: data.make || "",
            model: data.model || "",
            color: data.color || "",
          });
        });

        if (cancelled) return;
        setVehicles(vehicleList);
        console.log("PORTAL_RENDER_VEHICLES_COUNT", {
          count: vehicleList.length,
          vehicles: vehicleList.map(v => ({ id: v.id, plate: v.plate, make: v.make, model: v.model, color: v.color })),
        });
      } catch (error: any) {
        // Swallow errors that arrive after sign-out / screen change — those
        // are expected (Firestore rejects reads from a signed-out client).
        // Without this guard, signing out triggers a noisy
        // `PORTAL_READ_VEHICLES_ERROR Missing or insufficient permissions`
        // and a load-error banner that briefly flashes on the way to login.
        if (cancelled) return;
        console.error("PORTAL_READ_VEHICLES_ERROR", error);
        if (error?.code === "permission-denied") {
          setLoadError("Unable to load vehicles. Please contact concierge.");
        } else {
          setLoadError("Unable to load vehicles. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    // Strict gate: only run when ALL of auth + resident + building +
    // home-screen are present. Adding `user` to the gate (was missing
    // previously) is what stops the post-sign-out re-fetch — when sign-out
    // clears `user` first, this effect re-runs with the gate false and
    // never calls `fetchVehicles`.
    if (user && residentId && buildinggId && screen === "vehicle-select") {
      fetchVehicles();
    }

    return () => {
      cancelled = true;
    };
  }, [user, buildinggId, screen, residentId]);

  // Fetch services
  useEffect(() => {
    async function fetchServices() {
      console.log("LOAD_SERVICES_START", { buildinggId, screen });
      if (!buildinggId) {
        console.log("LOAD_SERVICES_SKIP: No buildinggId");
        return;
      }

      setLoading(true);
      const serviceList: CarWashService[] = [];

      try {
        // First try services collection
        console.log("LOAD_SERVICES_QUERYING", { path: `locations/${buildinggId}/services` });
        const servicesRef = collection(db, "locations", buildinggId, "services");
        const q = query(servicesRef, where("active", "==", true));
        const querySnapshot = await getDocs(q);
        console.log("LOAD_SERVICES_SUCCESS", { count: querySnapshot.size, buildinggId });

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log("LOAD_SERVICES_DOC", { id: doc.id, name: data.name, active: data.active });
          serviceList.push({
            id: doc.id,
            name: data.name || "",
            description: data.description || "",
            durationMinutes: data.durationMinutes || 0,
            priceCents: data.priceCents || 0,
            active: data.active || true,
          });
        });

        console.log("LOAD_SERVICES_INITIAL_LIST", { count: serviceList.length });

        // If no services, fallback to washPackages
        if (serviceList.length === 0) {
          console.log("LOAD_SERVICES_FALLBACK_TO_WASH_PACKAGES", { buildinggId });
          const packagesRef = collection(db, "locations", buildinggId, "washPackages");
          const packagesQuery = query(packagesRef, where("active", "==", true));
          const packagesSnapshot = await getDocs(packagesQuery);
          console.log("LOAD_WASH_PACKAGES_SUCCESS", { count: packagesSnapshot.size, buildinggId });

          packagesSnapshot.forEach((doc) => {
            const data = doc.data();
            console.log("LOAD_WASH_PACKAGES_DOC", { id: doc.id, name: data.name, active: data.active, priceCents: data.priceCents, price: data.price });
            serviceList.push({
              id: doc.id,
              name: data.name || "",
              description: data.description || "",
              durationMinutes: data.estimatedDuration || data.durationMinutes || 0,
              priceCents: typeof data.priceCents === "number"
                ? data.priceCents
                : Math.round((Number(data.price) || 0) * 100),
              active: data.active ?? true,
            });
          });
        }

        console.log("LOAD_SERVICES_FINAL_LIST", { count: serviceList.length, services: serviceList.map(s => ({ id: s.id, name: s.name, active: s.active })) });
        setServices(serviceList);
      } catch (error) {
        console.error("LOAD_SERVICES_ERROR", error);
      } finally {
        setLoading(false);
      }
    }

    if (buildinggId && screen === "service-select") {
      fetchServices();
    }
  }, [buildinggId, screen]);

  // Fetch available slots for selected service and date
  useEffect(() => {
    async function fetchSlots() {
      if (!selectedService || !selectedDate || screen !== "slot-select") {
        return;
      }

      setLoading(true);
      setSelectedSlot(null);

      try {
        const result = await api.getAvailableCarWashSlots(selectedService.id, selectedDate);
        console.log("FETCH_SLOTS_RESULT", result);

        if (result.slots) {
          setSlots(result.slots);
        }
      } catch (error) {
        console.error("FETCH_SLOTS_ERROR", error);
        setToastMessage({ type: "error", message: "Unable to fetch available slots." });
      } finally {
        setLoading(false);
      }
    }

    fetchSlots();
  }, [selectedService, selectedDate, screen]);

  // Fetch resident bookings
  useEffect(() => {
    async function fetchBookings() {
      if (!buildinggId || screen !== "bookings") {
        return;
      }

      setBookingsLoading(true);

      try {
        const result = await api.getResidentBookings();
        console.log("FETCH_BOOKINGS_RESULT", result);

        if (result.bookings) {
          setBookings(result.bookings);
        }
      } catch (error) {
        console.error("FETCH_BOOKINGS_ERROR", error);
        setToastMessage({ type: "error", message: "Unable to fetch bookings." });
      } finally {
        setBookingsLoading(false);
      }
    }

    fetchBookings();
  }, [buildinggId, screen]);

  // Add vehicle handler
  async function handleAddVehicle(payload: NewVehiclePayload) {
    console.log("ADD_VEHICLE_WRITE_START", { buildinggId, residentId, payload });
    try {
      const result = await api.createResidentVehicle(payload);
      console.log("ADD_VEHICLE_RESULT", result);

      setAddVehicleSuccess(true);

      // Refresh vehicles list
      if (!residentId || !buildinggId) return;

      const linksRef = collection(db, "locations", buildinggId, "residentVehicles");
      const linksQuery = query(
        linksRef,
        where("residentId", "==", residentId),
        where("isActive", "==", true)
      );
      const linksSnapshot = await getDocs(linksQuery);

      const vehicleIds: string[] = [];
      const vehicleIdToPlate = new Map<string, string>();
      linksSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.vehicleId) {
          vehicleIds.push(data.vehicleId);
          if (data.licensePlate) {
            vehicleIdToPlate.set(data.vehicleId, data.licensePlate);
          }
        }
      });

      if (vehicleIds.length > 0) {
        const vehiclesRef = collection(db, "locations", buildinggId, "vehicles");
        const vehiclesQuery = query(
          vehiclesRef,
          where(documentId(), "in", vehicleIds.length <= 10 ? vehicleIds : vehicleIds.slice(0, 10))
        );
        const vehiclesSnapshot = await getDocs(vehiclesQuery);

        const vehicleList: Vehicle[] = [];
        vehiclesSnapshot.forEach((doc) => {
          const data = doc.data();
          vehicleList.push({
            id: doc.id,
            plate: data.licensePlate || vehicleIdToPlate.get(doc.id) || "",
            make: data.make || "",
            model: data.model || "",
            color: data.color || "",
          });
        });

        setVehicles(vehicleList);

        // Auto-select newly added vehicle
        const newVehicle = vehicleList.find(v => v.plate === payload.licensePlate.toUpperCase());
        if (newVehicle) {
          setSelectedVehicle(newVehicle);
          console.log("ADD_VEHICLE_AUTO_SELECT", { vehicleId: newVehicle.id, plate: newVehicle.plate });
        }
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setAddVehicleSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("ADD_VEHICLE_ERROR", error);
      throw error;
    }
  }

  // Edit vehicle handler
  function handleEditVehicle(vehicle: Vehicle) {
    setVehicleToEdit(vehicle);
    setEditForm({
      vehicleId: vehicle.id,
      licensePlate: vehicle.plate,
      make: vehicle.make,
      model: vehicle.model,
      color: vehicle.color,
    });
    setEditModalOpen(true);
  }

  // Save edited vehicle
  async function handleSaveEdit() {
    if (!editForm || !residentId || !buildinggId) return;

    setIsEditing(true);
    try {
      const result = await api.updateResidentVehicle(editForm);
      console.log("UPDATE_VEHICLE_RESULT", result);

      // Refresh vehicles list
      const linksRef = collection(db, "locations", buildinggId, "residentVehicles");
      const linksQuery = query(
        linksRef,
        where("residentId", "==", residentId),
        where("isActive", "==", true)
      );
      const linksSnapshot = await getDocs(linksQuery);

      const vehicleIds: string[] = [];
      const vehicleIdToPlate = new Map<string, string>();
      linksSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.vehicleId) {
          vehicleIds.push(data.vehicleId);
          if (data.licensePlate) {
            vehicleIdToPlate.set(data.vehicleId, data.licensePlate);
          }
        }
      });

      if (vehicleIds.length > 0) {
        const vehiclesRef = collection(db, "locations", buildinggId, "vehicles");
        const vehiclesQuery = query(
          vehiclesRef,
          where(documentId(), "in", vehicleIds.length <= 10 ? vehicleIds : vehicleIds.slice(0, 10))
        );
        const vehiclesSnapshot = await getDocs(vehiclesQuery);

        const vehicleList: Vehicle[] = [];
        vehiclesSnapshot.forEach((doc) => {
          const data = doc.data();
          vehicleList.push({
            id: doc.id,
            plate: data.licensePlate || vehicleIdToPlate.get(doc.id) || "",
            make: data.make || "",
            model: data.model || "",
            color: data.color || "",
          });
        });

        setVehicles(vehicleList);

        // Update selected vehicle if it was edited
        if (selectedVehicle?.id === editForm.vehicleId) {
          const updatedVehicle = vehicleList.find(v => v.id === editForm.vehicleId);
          if (updatedVehicle) {
            setSelectedVehicle(updatedVehicle);
          }
        }
      }

      setEditModalOpen(false);
      setToastMessage({ type: "success", message: "Vehicle updated successfully." });
    } catch (error: any) {
      console.error("UPDATE_VEHICLE_ERROR", error);
      setToastMessage({ type: "error", message: error?.message || "Unable to update vehicle." });
    } finally {
      setIsEditing(false);
    }
  }

  // Delete vehicle handler
  function handleDeleteVehicle(vehicle: Vehicle) {
    console.log("DELETE_VEHICLE_HANDLER_CALLED", {
      vehicleId: vehicle.id,
      vehiclePlate: vehicle.plate,
      vehiclesCount: vehicles.length,
      allVehicleIds: vehicles.map(v => ({ id: v.id, plate: v.plate })),
    });
    setVehicleToDelete(vehicle);
    setDeleteModalOpen(true);
  }

  // Confirm delete vehicle
  async function handleConfirmDelete() {
    if (!vehicleToDelete || !residentId || !buildinggId) return;

    setIsEditing(true);
    try {
      const result = await api.deleteResidentVehicle(vehicleToDelete.id);
      console.log("DELETE_VEHICLE_RESULT", result);

      // Refresh vehicles list - remove deleted vehicle from state
      setVehicles(prev => prev.filter(v => v.id !== vehicleToDelete.id));

      // Clear selected vehicle if it was deleted
      if (selectedVehicle?.id === vehicleToDelete.id) {
        setSelectedVehicle(null);
      }

      setDeleteModalOpen(false);
      setToastMessage({ type: "success", message: "Vehicle removed successfully." });
    } catch (error: any) {
      console.error("DELETE_VEHICLE_ERROR", error);
      setToastMessage({ type: "error", message: error?.message || "Unable to remove vehicle." });
    } finally {
      setIsEditing(false);
    }
  }

  function handleLogout() {
    logout();
    setScreen("login");
    setEmail("");
    setPassword("");
    setVehicles([]);
    setSelectedVehicle(null);
    setSelectedService(null);
    setSelectedSlot(null);
    setSlots([]);
    setBookingSuccess(false);
  }

  // Login form handler
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    setLoginError(undefined);
    setSubmitting(true);

    try {
      const result = await login(email, password);
      if (!result.success && result.error) {
        setLoginError(result.error);
      }
    } catch (err: any) {
      setLoginError(err?.message || "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Screen transition helpers
  const goToNextStep = () => {
    switch (screen) {
      case "vehicle-select":
        if (selectedVehicle) {
          setSelectedService(null);
          setSelectedSlot(null);
          setScreen("service-select");
        }
        break;
      case "service-select":
        if (selectedService) {
          setSelectedSlot(null);
          setScreen("slot-select");
        }
        break;
      case "slot-select":
        if (selectedSlot) {
          setScreen("confirmation");
        }
        break;
      case "confirmation":
        setBookingSuccess(true);
        setScreen("success");
        break;
    }
  };

  const goToPreviousStep = () => {
    switch (screen) {
      case "service-select":
        setScreen("vehicle-select");
        break;
      case "slot-select":
        setScreen("service-select");
        break;
      case "confirmation":
        setScreen("slot-select");
        break;
    }
  };

  // Map screen to progress step
  const getProgressStep = (): keyof typeof STEPS => {
    switch (screen) {
      case "vehicle-select":
      case "add-vehicle":
        return "SELECT_VEHICLE";
      case "service-select":
        return "SELECT_SERVICE";
      case "slot-select":
        return "SELECT_TIME_SLOT";
      case "confirmation":
        return "CONFIRMATION";
      case "success":
        return "SUCCESS";
      default:
        return "SELECT_VEHICLE";
    }
  };

  // Render logic
  if (authLoading || (loading && screen !== "vehicle-select" && screen !== "service-select")) {
    console.log("APP_RETURN_LOADING", { loading, authLoading });
    return (
      <StrictMode>
        <div style={{ margin: 0, fontFamily: "Inter, sans-serif", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: "18px", color: "var(--stone)" }}>
            Loading...
          </div>
        </div>
      </StrictMode>
    );
  }

  if (!user) {
    console.log("APP_RETURN_LOGIN", { userUid: null });
    return (
      <StrictMode>
        <PortalLogin
          email={email}
          password={password}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onLogin={handleLogin}
          loading={loading}
          submitting={submitting}
          error={loginError}
        />
      </StrictMode>
    );
  }

  if (!residentId || !locationId) {
    // authLoading is guaranteed false here (handled above), so this is a
    // resolved outcome, not a pending one: the authenticated account has no
    // resident profile/claims for this portal. Show a terminal state instead
    // of leaving the user in an unrecoverable "loading" screen.
    console.log("APP_RETURN_NO_RESIDENT_PROFILE", { hasUser: true });
    return (
      <StrictMode>
        <div style={{ margin: 0, fontFamily: "Inter, sans-serif", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--ink)", marginBottom: "12px" }}>
              No resident profile found for this account.
            </div>
            <p style={{ fontSize: "14px", color: "var(--stone)", lineHeight: 1.5, marginBottom: "28px" }}>
              This account isn't linked to a resident profile at this property.
              If you believe this is a mistake, contact your building's support team.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "stretch" }}>
              <button className="btn-primary" onClick={handleLogout}>Sign Out</button>
              <a
                className="btn-ghost"
                href="mailto:hello@crastu.com"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", height: "44px" }}
              >
                Contact support
              </a>
              <button className="btn-ghost" onClick={handleLogout}>Try a different account</button>
            </div>
          </div>
        </div>
      </StrictMode>
    );
  }

  console.log("APP_RETURN_MAIN_PORTAL", {
    screen,
    buildinggId,
    residentId,
    locationId,
  });

  return (
    <StrictMode>
      <div style={{ margin: 0, fontFamily: "Inter, sans-serif", minHeight: "100vh", background: "var(--bg)", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <PortalHeader moduleName="Atrium Resident Portal" variant="light" onSignOut={handleLogout} showNavigation />

        {/* Main content */}
        <main style={{ maxWidth: "900px", margin: "0 auto", padding: "var(--space-xl)", flex: 1, width: "100%" }}>
          {/* Progress Step Bar — hidden on home and non-booking screens */}
          {!bookingSuccess && screen !== "vehicle-select" && screen !== "feedback" && (
            <ProgressStepBar currentStep={getProgressStep()} />
          )}

          {/* Toast notification */}
          {toastMessage && (
            <div style={{
              position: "fixed",
              bottom: "var(--space-xl)",
              left: "50%",
              transform: "translateX(-50%)",
              padding: "12px 20px",
              borderRadius: "8px",
              background: toastMessage.type === "success"
                ? "var(--emerald-500)"
                : "var(--danger)",
              color: "var(--white)",
              fontSize: "14px",
              fontWeight: 500,
              zIndex: 2000,
              boxShadow: "var(--shadow-lg)",
              animation: "fadeIn 0.2s ease",
            }}>
              {toastMessage.type === "success" ? "✓ " : "✕ "}
              {toastMessage.message}
            </div>
          )}

          {/* Screen content */}
          {/*
            Resident home (vehicle-select screen).

            All service cards and CTAs are gated by `portalSettings`. There is
            NO always-visible premium service. Every gate maps to a single
            flag in the canonical ResidentPortalSettings shape so that, when
            backend wires `getResidentPortalSettings(locationId)`, no UI logic
            needs to change — only the state setter.

            Gates (each must be explicitly true to render):
              Request my vehicle   ← valetRequests.enabled && valetRequests.allowRequestVehicle
              Schedule Car Wash    ← carWash.enabled && carWash.allowBooking
              My Requests          ← valetRequests.enabled && valetRequests.allowHistory
              My Bookings          ← bookings.enabled
              Payments & Receipts  ← payments.enabled && payments.allowReceipts && payments.provider
              EV Charging          ← evCharging.enabled && evCharging.allowResidentRequests
              Guest Parking        ← guestParking.enabled && guestParking.allowBooking

            With `portalSettings === null` (current reality — no backend
            source), every flag evaluates to false and the home renders the
            "Available services depend on your building settings." fallback.
          */}
          {screen === "vehicle-select" && (() => {
            // ── Settings-driven gates (all default-false until backend wires source) ──
            const canRequestVehicle =
              portalSettings?.valetRequests?.enabled === true &&
              portalSettings?.valetRequests?.allowRequestVehicle === true;
            const canBookCarWash =
              portalSettings?.carWash?.enabled === true &&
              portalSettings?.carWash?.allowBooking === true;
            const canViewRequestHistory =
              portalSettings?.valetRequests?.enabled === true &&
              portalSettings?.valetRequests?.allowHistory === true;
            const canViewBookings =
              portalSettings?.bookings?.enabled === true;
            const canCreateVehicle =
              portalSettings?.allowResidentVehicleCreation === true;
            const canSeePayments =
              portalSettings?.payments?.enabled === true &&
              portalSettings?.payments?.allowReceipts === true &&
              !!portalSettings?.payments?.provider;
            const canRequestEvCharging =
              portalSettings?.evCharging?.enabled === true &&
              portalSettings?.evCharging?.allowResidentRequests === true;
            const canBookGuestParking =
              portalSettings?.guestParking?.enabled === true &&
              portalSettings?.guestParking?.allowBooking === true;
            // Feedback: show unless settings explicitly disable it.
            // If portalSettings is null or missing the feedback key, defaults to visible
            // and the backend failed-precondition handles the unavailable case.
            const canLeaveFeedback =
              portalSettings?.feedback?.residentPortalEnabled !== false;

            const hasVehicles = vehicles.length > 0;
            const primaryDisabled = !canRequestVehicle || !hasVehicles;
            const primarySubtitle = !hasVehicles
              ? "Add a vehicle to get started"
              : selectedVehicle
                ? `${[selectedVehicle.make, selectedVehicle.model].filter(Boolean).join(" ").trim() || "Selected vehicle"}${selectedVehicle.plate ? ` · ${selectedVehicle.plate}` : ""}`
                : "Select a vehicle below to continue";

            const cardBase: React.CSSProperties = {
              background: "var(--white)",
              borderRadius: "16px",
              border: "1px solid var(--line)",
              padding: "20px",
              boxShadow: "0 1px 2px rgba(11,18,32,0.04)",
            };

            const serviceCardStyle: React.CSSProperties = {
              ...cardBase,
              display: "flex",
              alignItems: "center",
              gap: "14px",
              cursor: "pointer",
              transition: "box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
              textAlign: "left",
              width: "100%",
              font: "inherit",
              color: "inherit",
            };
            const onServiceHover = (e: React.MouseEvent<HTMLButtonElement>) => {
              const el = e.currentTarget;
              el.style.boxShadow = "0 6px 20px rgba(10,13,18,0.10)";
              el.style.borderColor = "var(--azzurro-300)";
              el.style.transform = "translateY(-1px)";
            };
            const onServiceLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
              const el = e.currentTarget;
              el.style.boxShadow = "0 1px 2px rgba(11,18,32,0.04)";
              el.style.borderColor = "var(--line)";
              el.style.transform = "translateY(0)";
            };

            const serviceIcon = (svg: React.ReactNode): React.CSSProperties & { node: React.ReactNode } => ({
              node: svg,
            } as any);
            void serviceIcon;

            const chevron = (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            );

            const sectionHeader = (title: string, count?: number) => (
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
                <h3 style={{
                  fontSize: "13px", fontWeight: 700, color: "var(--text-muted)",
                  margin: 0, letterSpacing: "0.06em", textTransform: "uppercase",
                }}>{title}</h3>
                {typeof count === "number" && count > 0 ? (
                  <span style={{
                    fontSize: "12px", fontWeight: 600, color: "var(--azzurro-700)",
                    background: "var(--azzurro-50)", padding: "2px 9px", borderRadius: "999px",
                  }}>{count}</span>
                ) : null}
              </div>
            );

            const welcomeName = user?.email ? user.email.split("@")[0] : null;

            return (
            <div style={{ marginTop: "var(--space-xl)" }}>
              {/* ── Welcome header ────────────────────────────────────── */}
              <div style={{ marginBottom: "24px" }}>
                <h2 style={{
                  fontSize: "26px", fontWeight: 600, color: "var(--ink)",
                  margin: "0 0 6px", letterSpacing: "-0.02em",
                  fontFamily: "var(--font-display)",
                }}>
                  Welcome{welcomeName ? `, ${welcomeName}` : ""}
                </h2>
                <p style={{ fontSize: "14px", color: "var(--text-muted)", margin: 0 }}>
                  {user?.email ?? "Your resident services, all in one place."}
                </p>
              </div>

              {/* ── Banners (preserved) ──────────────────────────────── */}
              {loadError && (
                <div role="alert" style={{
                  padding: "12px 16px", borderRadius: "10px", marginBottom: "16px",
                  background: "rgba(220,38,38,0.07)", color: "var(--danger)", fontSize: "14px",
                  border: "1px solid rgba(220,38,38,0.18)",
                }}>{loadError}</div>
              )}
              {addVehicleSuccess && (
                <div style={{
                  padding: "12px 16px", borderRadius: "10px", marginBottom: "16px",
                  background: "rgba(16,185,129,0.08)", color: "#065f46",
                  fontSize: "14px", fontWeight: 500,
                  border: "1px solid rgba(16,185,129,0.22)",
                }}>✓ Vehicle added successfully</div>
              )}
              {loading && (
                <div style={{
                  padding: "12px 16px", borderRadius: "10px", marginBottom: "16px",
                  background: "var(--azzurro-50)", color: "var(--azzurro-700)",
                  fontSize: "14px",
                  border: "1px solid var(--azzurro-200)",
                }}>Loading your vehicles…</div>
              )}

              {/* ── Primary action — only when settings allow vehicle requests ── */}
              {canRequestVehicle && (
              <button
                type="button"
                onClick={primaryDisabled ? undefined : () => setScreen("valet-request")}
                disabled={primaryDisabled}
                style={{
                  width: "100%", textAlign: "left",
                  display: "flex", alignItems: "center", gap: "16px",
                  padding: "22px 22px",
                  borderRadius: "18px",
                  border: "1px solid transparent",
                  background: primaryDisabled ? "var(--sand)" : "var(--azzurro-700)",
                  color: primaryDisabled ? "var(--text-muted)" : "var(--white)",
                  boxShadow: primaryDisabled ? "none" : "0 8px 24px rgba(47,95,137,0.18)",
                  cursor: primaryDisabled ? "not-allowed" : "pointer",
                  transition: "transform 0.12s ease, box-shadow 0.15s ease, background 0.15s ease",
                  marginBottom: "28px",
                  font: "inherit",
                }}
                onMouseEnter={e => {
                  if (primaryDisabled) return;
                  (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 12px 28px rgba(47,95,137,0.24)";
                }}
                onMouseLeave={e => {
                  if (primaryDisabled) return;
                  (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 24px rgba(47,95,137,0.18)";
                }}
                aria-label="Request my vehicle"
              >
                <span style={{
                  width: "46px", height: "46px", borderRadius: "12px",
                  background: primaryDisabled ? "var(--line)" : "rgba(255,255,255,0.18)",
                  color: primaryDisabled ? "var(--text-muted)" : "var(--white)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-4h12l2 4h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2" />
                    <circle cx="7.5" cy="17.5" r="2.5" /><circle cx="16.5" cy="17.5" r="2.5" />
                  </svg>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em" }}>
                    Request my vehicle
                  </span>
                  <span style={{
                    display: "block", marginTop: "4px", fontSize: "13.5px",
                    opacity: primaryDisabled ? 1 : 0.85, lineHeight: 1.4,
                  }}>
                    {primarySubtitle}
                  </span>
                </span>
                {!primaryDisabled ? (
                  <span style={{ display: "inline-flex", alignItems: "center", opacity: 0.9 }}>
                    {chevron}
                  </span>
                ) : null}
              </button>
              )}

              {/* ── Your vehicles ────────────────────────────────────── */}
              <section style={{ marginBottom: "28px" }}>
                {sectionHeader("Your vehicles", hasVehicles ? vehicles.length : undefined)}
                {!hasVehicles ? (
                  <div style={{
                    ...cardBase,
                    padding: "26px 20px",
                    textAlign: "center",
                    borderStyle: "dashed",
                  }}>
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--text-muted)", opacity: 0.6, marginBottom: "8px" }}>
                      <path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-4h12l2 4h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2" />
                      <circle cx="7.5" cy="17.5" r="2.5" /><circle cx="16.5" cy="17.5" r="2.5" />
                    </svg>
                    <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: 600, marginBottom: "4px" }}>
                      No vehicles on file yet
                    </div>
                    <div style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: canCreateVehicle ? "14px" : 0, lineHeight: 1.5 }}>
                      {canCreateVehicle
                        ? "Add a vehicle to start requesting valet and car wash services."
                        : "Your building hasn't enabled resident vehicle entry. Reach out to your front desk if you need a vehicle added."}
                    </div>
                    {canCreateVehicle && (
                      <button
                        onClick={() => setScreen("add-vehicle")}
                        className="btn-primary"
                        style={{
                          height: "auto", padding: "10px 18px", fontSize: "13.5px",
                          borderRadius: "10px", display: "inline-flex",
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                        </svg>
                        Add a vehicle
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {vehicles.map((vehicle) => (
                        <VehicleCard
                          key={vehicle.id}
                          vehicle={vehicle}
                          isSelected={selectedVehicle?.id === vehicle.id}
                          onSelect={() => setSelectedVehicle(vehicle)}
                          onEdit={() => handleEditVehicle(vehicle)}
                          onDelete={() => handleDeleteVehicle(vehicle)}
                        />
                      ))}
                    </div>
                    {canCreateVehicle && (
                      <button
                        onClick={() => setScreen("add-vehicle")}
                        style={{
                          marginTop: "12px",
                          display: "inline-flex", alignItems: "center", gap: "8px",
                          padding: "10px 14px",
                          background: "transparent",
                          color: "var(--azzurro-700)",
                          border: "1px dashed var(--azzurro-300)",
                          borderRadius: "10px",
                          fontSize: "13.5px", fontWeight: 600,
                          cursor: "pointer", font: "inherit",
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                        </svg>
                        Add another vehicle
                      </button>
                    )}
                  </>
                )}
              </section>

              {/* ── Services (rendered dynamically from settings) ─────── */}
              <section>
                {sectionHeader("Services")}
                {(() => {
                  // Build the list from gates only. Each entry MUST have a
                  // wired in-app route AND a true settings flag to be included.
                  // EV Charging / Guest Parking / Payments are placeholders
                  // here — when their flags AND their routes exist, push them
                  // into the array (no fake nav today).
                  interface ServiceDef {
                    id: string;
                    label: string;
                    subtitle: string;
                    icon: React.ReactNode;
                    onClick: () => void;
                  }
                  const services: ServiceDef[] = [];

                  if (canBookCarWash) {
                    services.push({
                      id: "carwash",
                      label: "Schedule a Car Wash",
                      subtitle: "Interior or exterior detailing",
                      icon: (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12h16M4 12a8 8 0 0 1 16 0M4 12a8 8 0 0 0 16 0M12 4v2M12 18v2M7 7l1.5 1.5M15.5 15.5 17 17M17 7l-1.5 1.5M8.5 15.5 7 17" />
                        </svg>
                      ),
                      onClick: () => setScreen("service-select"),
                    });
                  }
                  if (canViewRequestHistory) {
                    services.push({
                      id: "requests",
                      label: "My Requests",
                      subtitle: "Track valet and service requests",
                      icon: (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      ),
                      onClick: () => setScreen("valet-requests"),
                    });
                  }
                  if (canViewBookings) {
                    services.push({
                      id: "bookings",
                      label: "My Bookings",
                      subtitle: "View upcoming and past service bookings.",
                      icon: (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      ),
                      onClick: () => setScreen("bookings"),
                    });
                  }
                  // Vehicle creation deliberately lives ONLY in the Vehicles
                  // section above ("+ Add another vehicle" / "Add a vehicle"
                  // buttons gated by canCreateVehicle). Surfacing it as a
                  // Services card too created a duplicate action — removed.
                  if (canLeaveFeedback) {
                    services.push({
                      id: "feedback",
                      label: "Feedback",
                      subtitle: "Rate your experience or report an issue",
                      icon: (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      ),
                      onClick: () => setScreen("feedback"),
                    });
                  }
                  // canSeePayments / canRequestEvCharging / canBookGuestParking
                  // are gated but NOT pushed today — their in-app routes don't
                  // exist yet. When the routes are wired AND backend settings
                  // expose the flags, push them here.
                  void canSeePayments;
                  void canRequestEvCharging;
                  void canBookGuestParking;

                  if (portalSettingsLoading) {
                    return (
                      <div style={{
                        ...cardBase,
                        padding: "22px",
                        textAlign: "center",
                        fontSize: "13px", color: "var(--text-muted)",
                      }}>
                        Loading your services…
                      </div>
                    );
                  }

                  if (services.length === 0) {
                    return (
                      <div style={{
                        ...cardBase,
                        padding: "26px 20px",
                        textAlign: "center",
                        borderStyle: "dashed",
                      }}>
                        <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: 600, marginBottom: "4px" }}>
                          Available services depend on your building settings.
                        </div>
                        <div style={{ fontSize: "12.5px", color: "var(--text-muted)", lineHeight: 1.5 }}>
                          Ask your front desk to enable additional resident services.
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                      gap: "12px",
                    }}>
                      {services.map((svc) => (
                        <button
                          key={svc.id}
                          type="button"
                          onClick={svc.onClick}
                          style={serviceCardStyle}
                          onMouseEnter={onServiceHover}
                          onMouseLeave={onServiceLeave}
                          aria-label={svc.label}
                        >
                          <span style={{
                            width: "40px", height: "40px", borderRadius: "10px",
                            background: "var(--azzurro-50)", color: "var(--azzurro-700)",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            {svc.icon}
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: "block", fontSize: "15px", fontWeight: 600, color: "var(--ink)" }}>{svc.label}</span>
                            <span style={{ display: "block", fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px", lineHeight: 1.4 }}>
                              {svc.subtitle}
                            </span>
                          </span>
                          <span style={{ color: "var(--text-muted)" }}>{chevron}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </section>
            </div>
            );
          })()}

          {screen === "add-vehicle" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <button
                  onClick={() => setScreen("vehicle-select")}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    color: "var(--stone)",
                  }}
                >
                  ← Back
                </button>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  Add New Vehicle
                </h2>
              </div>

              <AddVehicleForm
                existingVehicles={vehicles as VehicleSuggestion[]}
                onSubmitVehicle={handleAddVehicle}
                onCancel={() => setScreen("vehicle-select")}
              />
            </div>
          )}

          {screen === "feedback" && locationId && (
            <ResidentFeedbackForm
              locationId={locationId}
              vehicles={vehicles}
              onBack={() => setScreen("vehicle-select")}
            />
          )}

          {screen === "service-select" && (() => {
            console.log("SERVICE_SELECT_SCREEN_RENDER", {
              buildinggId,
              loading,
              servicesCount: services.length,
              services: services.map(s => ({ id: s.id, name: s.name, active: s.active })),
              selectedVehicle: selectedVehicle?.id,
            });
            return null;
          })()}

          {screen === "service-select" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <button
                  onClick={goToPreviousStep}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    color: "var(--stone)",
                  }}
                >
                  ← Back
                </button>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  Select Service
                </h2>
              </div>

              <p style={{
                fontSize: "14px",
                color: "var(--stone-500)",
                marginBottom: "var(--space-lg)",
              }}>
                Choose a service for your {selectedVehicle?.make} {selectedVehicle?.model}.
              </p>

              {loading ? (
                <div style={{
                  padding: "var(--space-xl)",
                  textAlign: "center",
                  color: "var(--stone)",
                }}>
                  Loading services...
                </div>
              ) : services.length === 0 ? (
                <div style={{
                  padding: "var(--space-xl)",
                  textAlign: "center",
                  background: "var(--white)",
                  borderRadius: "var(--radius-md)",
                  border: "1px dashed var(--line)",
                }}>
                  <p style={{
                    fontSize: "14px",
                    color: "var(--stone)",
                  }}>
                    No services available at this time. Please contact concierge.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-sm)",
                  marginBottom: "var(--space-lg)",
                }}>
                  {services.map((svc) => (
                    <div
                      key={svc.id}
                      onClick={() => setSelectedService(svc)}
                      style={{
                        padding: "20px 24px",
                        borderRadius: "12px",
                        border: selectedService?.id === svc.id ? "2px solid var(--blue-500)" : "1px solid var(--line)",
                        background: selectedService?.id === svc.id ? "rgba(59, 130, 246, 0.04)" : "var(--white)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        boxShadow: selectedService?.id === svc.id ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedService?.id !== svc.id) {
                          e.currentTarget.style.borderColor = "var(--blue-300)";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedService?.id !== svc.id) {
                          e.currentTarget.style.borderColor = "var(--line)";
                          e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.05)";
                        }
                      }}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                        <div>
                          <div style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "var(--ink)",
                            marginBottom: "4px",
                          }}>
                            {svc.name}
                          </div>
                          <div style={{
                            fontSize: "13px",
                            color: "var(--stone-500)",
                          }}>
                            {svc.durationMinutes} min
                          </div>
                        </div>
                        <div style={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "var(--blue-600)",
                        }}>
                          ${(svc.priceCents / 100).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={goToNextStep}
                disabled={!selectedService}
                className="btn-primary"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  opacity: selectedService ? 1 : 0.5,
                  cursor: selectedService ? "pointer" : "not-allowed",
                }}
              >
                Continue
              </button>
            </div>
          )}

          {screen === "slot-select" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <button
                  onClick={goToPreviousStep}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    color: "var(--stone)",
                  }}
                >
                  ← Back
                </button>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  Select Date & Time
                </h2>
              </div>

              {/* Date Selection */}
              <div style={{ marginBottom: "var(--space-lg)" }}>
                <label style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "var(--ink)",
                  marginBottom: "8px",
                }}>
                  Select Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedSlot(null);
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "1px solid var(--line)",
                    borderRadius: "8px",
                    fontSize: "15px",
                  }}
                />
              </div>

              {/* Available Slots */}
              {loading ? (
                <div style={{
                  padding: "var(--space-xl)",
                  textAlign: "center",
                  color: "var(--stone)",
                }}>
                  Loading available slots...
                </div>
              ) : slots.length === 0 ? (
                <div style={{
                  padding: "var(--space-xl)",
                  textAlign: "center",
                  background: "var(--white)",
                  borderRadius: "var(--radius-md)",
                  border: "1px dashed var(--line)",
                }}>
                  <p style={{
                    fontSize: "14px",
                    color: "var(--stone)",
                  }}>
                    No available slots for this date. Please select a different date.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: "12px",
                  marginBottom: "var(--space-lg)",
                }}>
                  {slots.map((slot) => (
                    <div
                      key={slot.slotId}
                      onClick={() => {
                        if (slot.available) {
                          setSelectedSlot(slot);
                        }
                      }}
                      style={{
                        padding: "16px 12px",
                        borderRadius: "8px",
                        border: selectedSlot?.slotId === slot.slotId
                          ? "2px solid var(--blue-500)"
                          : "1px solid var(--line)",
                        background: selectedSlot?.slotId === slot.slotId
                          ? "rgba(59, 130, 246, 0.04)"
                          : slot.available
                          ? "var(--white)"
                          : "var(--bg-surface)",
                        cursor: slot.available ? "pointer" : "not-allowed",
                        opacity: slot.available ? 1 : 0.5,
                        textAlign: "center",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <div style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: slot.available ? "var(--ink)" : "var(--stone-500)",
                        marginBottom: "4px",
                      }}>
                        {slot.display}
                      </div>
                      {!slot.available && (
                        <div style={{
                          fontSize: "11px",
                          color: "var(--stone-400)",
                        }}>
                          Full
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={goToNextStep}
                disabled={!selectedSlot}
                className="btn-primary"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  opacity: selectedSlot ? 1 : 0.5,
                  cursor: selectedSlot ? "pointer" : "not-allowed",
                }}
              >
                {selectedSlot ? "Continue" : "Select a Time Slot"}
              </button>
            </div>
          )}

          {screen === "confirmation" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <button
                  onClick={goToPreviousStep}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    color: "var(--stone)",
                  }}
                >
                  ← Back
                </button>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  Confirm Booking
                </h2>
              </div>

              <div style={{
                background: "var(--white)",
                borderRadius: "12px",
                border: "1px solid var(--line)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-lg)",
              }}>
                <h3 style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  marginBottom: "var(--space-md)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}>
                  Booking Details
                </h3>

                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-md)",
                }}>
                  <div>
                    <div style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "var(--stone-400)",
                      marginBottom: "4px",
                    }}>
                      Vehicle
                    </div>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: 500,
                      color: "var(--ink)",
                    }}>
                      {selectedVehicle?.make} {selectedVehicle?.model}
                    </div>
                    <div style={{
                      fontSize: "14px",
                      color: "var(--stone-500)",
                    }}>
                      {selectedVehicle?.plate} • {selectedVehicle?.color}
                    </div>
                  </div>

                  <div style={{
                    borderTop: "1px solid var(--line)",
                    paddingTop: "var(--space-md)",
                  }}>
                    <div style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "var(--stone-400)",
                      marginBottom: "4px",
                    }}>
                      Package
                    </div>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: 500,
                      color: "var(--ink)",
                    }}>
                      {selectedService?.name}
                    </div>
                    <div style={{
                      fontSize: "14px",
                      color: "var(--stone-500)",
                    }}>
                      {selectedService?.durationMinutes} min • ${((selectedService?.priceCents ?? 0) / 100).toFixed(2)}
                    </div>
                  </div>

                  <div style={{
                    borderTop: "1px solid var(--line)",
                    paddingTop: "var(--space-md)",
                  }}>
                    <div style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "var(--stone-400)",
                      marginBottom: "4px",
                    }}>
                      Time
                    </div>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: 500,
                      color: "var(--stone-500)",
                    }}>
                      Time slot selection coming soon
                    </div>
                  </div>

                  <div style={{
                    borderTop: "1px solid var(--line)",
                    paddingTop: "var(--space-md)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "var(--ink)",
                    }}>
                      Total
                    </div>
                    <div style={{
                      fontSize: "20px",
                      fontWeight: 600,
                      color: "var(--blue-600)",
                    }}>
                      ${((selectedService?.priceCents ?? 0) / 100).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={goToNextStep}
                disabled
                className="btn-primary"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  opacity: 0.5,
                  cursor: "not-allowed",
                }}
              >
                Confirm Booking
              </button>
            </div>
          )}

          {screen === "success" && (
            <div style={{
              marginTop: "var(--space-xl)",
              textAlign: "center",
            }}>
              <div style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "rgba(16, 185, 129, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto var(--space-lg)",
              }}>
                <div style={{
                  fontSize: "40px",
                  color: "var(--emerald-500)",
                }}>
                  ✓
                </div>
              </div>

              <h2 style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "var(--ink)",
                marginBottom: "8px",
              }}>
                Booking Confirmed!
              </h2>

              <p style={{
                fontSize: "14px",
                color: "var(--stone-500)",
                marginBottom: "var(--space-xl)",
              }}>
                Your car wash has been scheduled. You'll receive a confirmation shortly.
              </p>

              <button
                onClick={() => {
                  setSelectedVehicle(null);
                  setSelectedService(null);
                  setSelectedSlot(null);
                  setBookingSuccess(false);
                  setScreen("vehicle-select");
                }}
                className="btn-primary"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                }}
              >
                Book Another Wash
              </button>
              <button
                onClick={() => {
                  setScreen("bookings");
                }}
                className="btn-secondary"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  marginTop: "var(--space-sm)",
                }}
              >
                View My Bookings
              </button>
            </div>
          )}

          {screen === "bookings" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  My Bookings
                </h2>
              </div>

              {bookingsLoading ? (
                <div style={{
                  padding: "var(--space-xl)",
                  textAlign: "center",
                  color: "var(--stone)",
                }}>
                  Loading bookings...
                </div>
              ) : bookings.length === 0 ? (
                <div style={{
                  padding: "var(--space-xl)",
                  textAlign: "center",
                  background: "var(--white)",
                  borderRadius: "var(--radius-md)",
                  border: "1px dashed var(--line)",
                }}>
                  <p style={{
                    fontSize: "14px",
                    color: "var(--stone)",
                  }}>
                    No upcoming bookings found.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-sm)",
                  marginBottom: "var(--space-lg)",
                }}>
                  {bookings.map((booking) => {
                    const bookingDate = new Date(booking.startTime);
                    const dateStr = bookingDate.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
                    const timeStr = bookingDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

                    return (
                      <div
                        key={booking.id}
                        style={{
                          padding: "16px 20px",
                          borderRadius: "12px",
                          border: "1px solid var(--line)",
                          background: "var(--white)",
                        }}
                      >
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}>
                          <div>
                            <div style={{
                              fontSize: "12px",
                              color: "var(--stone-400)",
                              marginBottom: "4px",
                            }}>
                              {dateStr} • {timeStr}
                            </div>
                            <div style={{
                              fontSize: "16px",
                              fontWeight: 600,
                              color: "var(--ink)",
                              marginBottom: "4px",
                            }}>
                              {booking.serviceName}
                            </div>
                            <div style={{
                              fontSize: "13px",
                              color: "var(--stone)",
                            }}>
                              {booking.vehicleInfo?.make} {booking.vehicleInfo?.model}
                            </div>
                            <div style={{
                              fontSize: "13px",
                              color: "var(--stone-500)",
                            }}>
                              {booking.vehicleInfo?.licensePlate}
                            </div>
                          </div>
                          <div style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "var(--blue-600)",
                          }}>
                            ${(booking.priceCents / 100).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => setScreen("vehicle-select")}
                className="btn-secondary"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                }}
              >
                Back to Home
              </button>
            </div>
          )}

          {screen === "valet-request" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <button
                  onClick={() => setScreen("vehicle-select")}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    color: "var(--stone)",
                  }}
                >
                  ← Back
                </button>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  Valet Request
                </h2>
              </div>

              <ValetRequestForm
                vehicles={vehicles}
                vehiclesLoading={loading}
                onSubmitSuccess={(requestId) => {
                  setValetRequestId(requestId);
                  setScreen("valet-request-success");
                }}
                onCancel={() => setScreen("vehicle-select")}
              />
            </div>
          )}

          {screen === "valet-request-success" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <ValetRequestSuccess
                requestId={valetRequestId}
                onDone={() => {
                  setValetRequestId("");
                  setScreen("vehicle-select");
                }}
              />
            </div>
          )}

          {screen === "valet-requests" && (
            <div style={{ marginTop: "var(--space-xl)" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
                marginBottom: "var(--space-lg)",
              }}>
                <button
                  onClick={() => setScreen("vehicle-select")}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    color: "var(--stone)",
                  }}
                >
                  ← Back
                </button>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>
                  Valet Requests
                </h2>
              </div>
              {locationId ? <ValetRequestsPage locationId={locationId} /> : <div>Loading...</div>}
            </div>
          )}
        </main>

        {/* Footer with version */}
        <footer style={{
          textAlign: "center",
          padding: "var(--space-lg) var(--space-xl)",
          borderTop: "1px solid var(--line)",
          color: "var(--stone-400)",
          fontSize: "12px",
        }}>
          Atrium Resident Portal v{PORTAL_VERSION}
        </footer>
      </div>

      {/* Edit Vehicle Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Vehicle"
        subtitle="Update your vehicle details below."
        actions={
          <>
            <button
              onClick={() => setEditModalOpen(false)}
              className="btn-secondary"
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isEditing || !editForm?.make || !editForm?.model}
              className="btn-primary"
              style={{
                flex: 1,
                opacity: (isEditing || !editForm?.make || !editForm?.model) ? 0.5 : 1,
                cursor: (isEditing || !editForm?.make || !editForm?.model) ? "not-allowed" : "pointer",
              }}
            >
              {isEditing ? "Saving..." : "Save Changes"}
            </button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}>
          <div>
            <label style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--ink)",
              marginBottom: "6px",
            }}>
              License Plate <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <input
              type="text"
              value={editForm?.licensePlate || ""}
              onChange={(e) => setEditForm(prev => prev ? { ...prev, licensePlate: e.target.value } : null)}
              placeholder="ABC123"
              maxLength={10}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: "var(--font-mono)",
              }}
            />
          </div>

          <div>
            <label style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--ink)",
              marginBottom: "6px",
            }}>
              Make <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <input
              type="text"
              value={editForm?.make || ""}
              onChange={(e) => setEditForm(prev => prev ? { ...prev, make: e.target.value } : null)}
              placeholder="e.g., Toyota"
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--ink)",
              marginBottom: "6px",
            }}>
              Model <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <input
              type="text"
              value={editForm?.model || ""}
              onChange={(e) => setEditForm(prev => prev ? { ...prev, model: e.target.value } : null)}
              placeholder="e.g., Camry"
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--ink)",
              marginBottom: "6px",
            }}>
              Color
            </label>
            <input
              type="text"
              value={editForm?.color || ""}
              onChange={(e) => setEditForm(prev => prev ? { ...prev, color: e.target.value } : null)}
              placeholder="e.g., White"
              style={{
                width: "100%",
                padding: "10px 14px",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Delete Vehicle Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          console.log("DELETE_MODAL_CLOSED");
          setDeleteModalOpen(false);
        }}
        title="Remove Vehicle"
        actions={
          <>
            <button
              onClick={() => {
                console.log("DELETE_MODAL_CANCEL_CLICKED");
                setDeleteModalOpen(false);
              }}
              className="btn-secondary"
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log("DELETE_MODAL_CONFIRM_CLICKED", {
                  vehicleToDeleteId: vehicleToDelete?.id,
                  vehicleToDeletePlate: vehicleToDelete?.plate,
                  vehicleToDeleteMake: vehicleToDelete?.make,
                  vehicleToDeleteModel: vehicleToDelete?.model,
                });
                handleConfirmDelete();
              }}
              disabled={isEditing}
              className="btn-primary"
              style={{
                flex: 1,
                background: "var(--danger)",
                opacity: isEditing ? 0.5 : 1,
                cursor: isEditing ? "not-allowed" : "pointer",
              }}
            >
              {isEditing ? "Removing..." : "Remove Vehicle"}
            </button>
          </>
        }
      >
        <p style={{
          fontSize: "14px",
          color: "var(--stone-500)",
          lineHeight: 1.5,
        }}>
          Are you sure you want to remove <strong>{vehicleToDelete?.plate}</strong> ({vehicleToDelete?.make} {vehicleToDelete?.model}) from your account?
        </p>
      </Modal>
    </StrictMode>
  );
}

function PortalLogin({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onLogin,
  loading,
  submitting,
  error,
}: {
  email: string;
  password: string;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onLogin: (e: React.FormEvent) => void;
  loading?: boolean;
  submitting?: boolean;
  error?: string;
}) {
  return (
    <PortalLoginCard
      email={email}
      password={password}
      onEmailChange={onEmailChange}
      onPasswordChange={onPasswordChange}
      onLogin={onLogin}
      loading={loading}
      submitting={submitting}
      error={error}
    />
  );
}

/* Add responsive styles */
const style = document.createElement("style");
style.textContent = `
  @media (min-width: 769px) {
    .desktop-only { display: grid !important; }
    .mobile-only { display: none !important; }
  }
  @media (max-width: 768px) {
    .desktop-only { display: none !important; }
    .mobile-only { display: flex !important; }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translate(-50%, 10px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }
`;
document.head.appendChild(style);

export default App;
